import type { AmbientLightBeamConfig, AmbientLightBeamWorldBasis } from './ambientShellBackground'
import {
  ambientLightBeamWorldFromTU,
  getAmbientLightBeamWorldBasis,
} from './ambientShellBackground'

/** Decoupled from main ambient particle preset; only lives in light shafts. */
export const AMBIENT_BEAM_MOTE_COUNT = 10

const MOTE_VT_MIN = 2
const MOTE_VT_MAX = 5.2
const MOTE_VU_MIN = 1.1
const MOTE_VU_MAX = 3.4
const MOTE_FADE_IN_MIN_MS = 3400
const MOTE_FADE_IN_SPREAD_MS = 4800
const MOTE_FULL_MIN_MS = 500
const MOTE_FULL_SPREAD_MS = 1_000
const MOTE_FADE_OUT_MIN_MS = 3400
const MOTE_FADE_OUT_SPREAD_MS = 5200
const MOTE_PEAK_ALPHA = 0.24
const MOTE_SIZE_MIN = 1
const MOTE_SIZE_MAX = 3
/** Spawns only where canvas Y is within this fraction of height from the top (bright shaft, before axial falloff). */
const BEAM_MOTE_SPAWN_MAX_Y_FRAC = 0.4
const BEAM_MOTE_SPAWN_ATTEMPTS = 36

function maxTAlongBeamCenterlineWithinMaxY(
  basis: AmbientLightBeamWorldBasis,
  L: number,
  yMax: number,
): number {
  if (L <= 0) {
    return 0
  }
  const yAtFoot = ambientLightBeamWorldFromTU(basis, L, 0).y
  const yAtCrack = ambientLightBeamWorldFromTU(basis, 0, 0).y
  if (yAtCrack > yMax) {
    return 0
  }
  if (yAtFoot <= yMax) {
    return L
  }
  let lo = 0
  let hi = L
  for (let i = 0; i < 22; i += 1) {
    const mid = (lo + hi) / 2
    const y = ambientLightBeamWorldFromTU(basis, mid, 0).y
    if (y <= yMax) {
      lo = mid
    } else {
      hi = mid
    }
  }
  return lo
}

function pickSpawnTU(
  basis: AmbientLightBeamWorldBasis,
  L: number,
  h: number,
  random: () => number,
): { t: number; u: number } {
  const yMax = h * BEAM_MOTE_SPAWN_MAX_Y_FRAC
  const tCap = maxTAlongBeamCenterlineWithinMaxY(basis, L, yMax)
  const tLo = Math.min(L * 0.02, tCap * 0.1)
  const tHi = Math.max(tLo, tCap * 0.97)

  for (let attempt = 0; attempt < BEAM_MOTE_SPAWN_ATTEMPTS; attempt += 1) {
    const t = tLo + random() * Math.max(0, tHi - tLo)
    const hwAtT = basis.hwTop + (basis.hwBot - basis.hwTop) * (L > 0 ? t / L : 0)
    const u = (random() * 2 - 1) * hwAtT * 0.85
    const pos = ambientLightBeamWorldFromTU(basis, t, u)
    if (pos.y <= yMax) {
      return { t, u }
    }
  }

  const t = tLo + (tHi - tLo) * 0.35
  return { t, u: 0 }
}

export type AmbientBeamMote = {
  ageMs: number
  beamIndex: number
  color: number
  fadeInMs: number
  fadeOutMs: number
  fullMs: number
  size: number
  t: number
  u: number
  vu: number
  vt: number
}

function easeInOutSine(value: number): number {
  const clamped = Math.min(1, Math.max(0, value))
  return -(Math.cos(Math.PI * clamped) - 1) / 2
}

function warmDustColor(random: () => number): number {
  const red = 255
  const green = 226 + Math.floor(random() * 22)
  const blue = 182 + Math.floor(random() * 38)
  return (red << 16) + (green << 8) + blue
}

function pickSignedSpeed(random: () => number, lo: number, hi: number): number {
  const sign = random() < 0.5 ? -1 : 1
  return sign * (lo + random() * Math.max(0, hi - lo))
}

export function createAmbientBeamMote(
  beams: AmbientLightBeamConfig[],
  w: number,
  h: number,
  random: () => number,
  nowMs: number,
  reducedMotion: boolean,
): AmbientBeamMote | null {
  if (beams.length === 0) {
    return null
  }

  const beamIndex = Math.min(beams.length - 1, Math.floor(random() * beams.length))
  const beam = beams[beamIndex]!
  const basis = getAmbientLightBeamWorldBasis(beam, w, h, nowMs, reducedMotion)
  const { L } = basis
  if (L <= 1) {
    return null
  }

  const { t, u } = pickSpawnTU(basis, L, h, random)

  const fadeInMs = MOTE_FADE_IN_MIN_MS + random() * MOTE_FADE_IN_SPREAD_MS
  const fullMs = MOTE_FULL_MIN_MS + random() * MOTE_FULL_SPREAD_MS
  const fadeOutMs = MOTE_FADE_OUT_MIN_MS + random() * MOTE_FADE_OUT_SPREAD_MS

  return {
    ageMs: 0,
    beamIndex,
    color: warmDustColor(random),
    fadeInMs,
    fadeOutMs,
    fullMs,
    size: MOTE_SIZE_MIN + random() * Math.max(0, MOTE_SIZE_MAX - MOTE_SIZE_MIN),
    t,
    u,
    vu: reducedMotion ? 0 : pickSignedSpeed(random, MOTE_VU_MIN, MOTE_VU_MAX),
    vt: reducedMotion ? 0 : pickSignedSpeed(random, MOTE_VT_MIN, MOTE_VT_MAX),
  }
}

export function getAmbientBeamMoteTotalMs(mote: AmbientBeamMote): number {
  return mote.fadeInMs + mote.fullMs + mote.fadeOutMs
}

export function getAmbientBeamMoteLifecycleAlpha(mote: AmbientBeamMote): number {
  if (mote.ageMs < mote.fadeInMs) {
    return easeInOutSine(mote.ageMs / mote.fadeInMs)
  }

  const fadeOutAt = mote.fadeInMs + mote.fullMs
  if (mote.ageMs < fadeOutAt) {
    return 1
  }

  return easeInOutSine(1 - (mote.ageMs - fadeOutAt) / mote.fadeOutMs)
}

export function rebuildAmbientBeamMotes(
  beams: AmbientLightBeamConfig[],
  w: number,
  h: number,
  random: () => number,
  nowMs: number,
  reducedMotion: boolean,
): AmbientBeamMote[] {
  if (beams.length === 0) {
    return []
  }

  const out: AmbientBeamMote[] = []
  for (let index = 0; index < AMBIENT_BEAM_MOTE_COUNT; index += 1) {
    const mote = createAmbientBeamMote(beams, w, h, random, nowMs, reducedMotion)
    if (mote) {
      mote.ageMs = random() * getAmbientBeamMoteTotalMs(mote)
      out.push(mote)
    }
  }
  return out
}

export function stepAmbientBeamMotes(
  motes: AmbientBeamMote[],
  beams: AmbientLightBeamConfig[],
  w: number,
  h: number,
  deltaSeconds: number,
  nowMs: number,
  reducedMotion: boolean,
  random: () => number,
): void {
  if (beams.length === 0) {
    motes.length = 0
    return
  }

  if (reducedMotion) {
    for (const mote of motes) {
      mote.vt = 0
      mote.vu = 0
    }
  }

  const dt = deltaSeconds

  for (let index = 0; index < motes.length; index += 1) {
    const mote = motes[index]!
    if (mote.beamIndex >= beams.length) {
      mote.beamIndex = Math.min(beams.length - 1, Math.floor(random() * beams.length))
    }

    const beam = beams[mote.beamIndex]!
    const basis = getAmbientLightBeamWorldBasis(beam, w, h, nowMs, reducedMotion)
    const { L } = basis

    if (!reducedMotion && L > 1) {
      mote.t += mote.vt * dt
      mote.u += mote.vu * dt
    }

    const tClamped = Math.max(0, Math.min(L, mote.t))
    const hwAtT =
      basis.hwTop + (basis.hwBot - basis.hwTop) * (L > 0 ? tClamped / L : 0)
    mote.t = tClamped
    mote.u = Math.max(-hwAtT, Math.min(hwAtT, mote.u))

    if (!reducedMotion && L > 1) {
      const eps = 0.4
      if (mote.t <= eps) {
        mote.vt = Math.abs(mote.vt)
      } else if (mote.t >= L - eps) {
        mote.vt = -Math.abs(mote.vt)
      }
      if (mote.u <= -hwAtT + eps) {
        mote.vu = Math.abs(mote.vu)
      } else if (mote.u >= hwAtT - eps) {
        mote.vu = -Math.abs(mote.vu)
      }
    }

    mote.ageMs += dt * 1000
    if (mote.ageMs >= getAmbientBeamMoteTotalMs(mote)) {
      const next = createAmbientBeamMote(beams, w, h, random, nowMs, reducedMotion)
      if (next) {
        motes[index] = next
      }
    }
  }
}

export function getAmbientBeamMoteWorldPosition(
  mote: AmbientBeamMote,
  beams: AmbientLightBeamConfig[],
  w: number,
  h: number,
  nowMs: number,
  reducedMotion: boolean,
): { x: number; y: number } | null {
  const beam = beams[mote.beamIndex]
  if (!beam) {
    return null
  }
  const basis = getAmbientLightBeamWorldBasis(beam, w, h, nowMs, reducedMotion)
  return ambientLightBeamWorldFromTU(basis, mote.t, mote.u)
}

export const AMBIENT_BEAM_MOTE_DRAW_ALPHA = MOTE_PEAK_ALPHA
